import { useState } from "react";
import { useReportsApproved } from "../../../hooks/useDashboardQueries";
import {
	DashboardReportType,
	ReportsApprovedResponse,
} from "../../../types/dashboard.types";
import { cardStatus } from "../cardStatus";
import TrendChart, { TrendDatum } from "../charts/TrendChart";
import DashboardCard from "../DashboardCard";
import {
	BADGES,
	formatCount,
	formatMonthKey,
	selectableYears,
	yearOptions,
} from "../dashboardConfig";
import { masterTenantName, TenantWidgetProps } from "../tenantChoice";
import { useExpandedView, useTenantChoice } from "../widgetHooks";
import WidgetSelect from "../WidgetSelect";
import {
	APPROVED_TITLE,
	approvedEmptyText,
	approvedTypes,
	hasNoApproved,
	sumTypes,
} from "./reportsApproved";
import ReportsApprovedExpanded from "./ReportsApprovedExpanded";

const TITLE = `${APPROVED_TITLE} by month`;

interface ApprovedTrendProps {
	data: ReportsApprovedResponse;
	onlyTypes?: DashboardReportType[];
	ownLabel: string;
	context: string;
}

function ApprovedTrend({
	data,
	onlyTypes,
	ownLabel,
	context,
}: ApprovedTrendProps) {
	const types = approvedTypes(data, onlyTypes);
	const keys = types.map((type) => type.key);
	// January to December; months that have not started yet stay blank.
	const points = data.byMonth.map((counts, index) => {
		const id = `${data.year}-${String(index + 1).padStart(2, "0")}`;
		const point: TrendDatum = { id, title: formatMonthKey(id, "MMM YYYY") };
		for (const type of keys) {
			point[type] = index < data.monthsElapsed ? (counts[type] ?? 0) : null;
		}
		return point;
	});
	const { own } = data;

	return (
		<TrendChart
			data={points}
			series={types}
			tickLabel={(point) => formatMonthKey(point.id, "MMM")}
			valueDetail={
				own
					? (point, type) => {
							const month = Number(point.id.slice(5)) - 1;
							const count =
								own.byMonth[month]?.[type as DashboardReportType] ?? 0;
							return `(${ownLabel} ${formatCount(count)})`;
						}
					: undefined
			}
			yAxisSide="left"
			legendSide="right"
			variant="flat"
			summary={`${TITLE}, ${context}: ${types
				.map((type) => `${type.label} ${sumTypes(data.byType, [type.key])}`)
				.join(", ")}.`}
		/>
	);
}

/** Approved reports per month of the selected year, one line per type. */
function ReportsApprovedTrendWidget({
	tenants,
	currentTenantKey,
}: TenantWidgetProps) {
	const { choice, tenant, setTenant, target, label } = useTenantChoice(
		tenants,
		"master",
		currentTenantKey
	);
	const [year, setYear] = useState(() => selectableYears()[0]);
	const expanded = useExpandedView();
	const query = useReportsApproved(target, year);
	const ownLabel = masterTenantName(tenants);

	return (
		<>
			<DashboardCard
				badge={BADGES.reports}
				title={TITLE}
				className="dashboard-widget--approved-trend dashboard-widget--short"
				controls={
					<>
						<WidgetSelect
							value={tenant}
							options={choice.options}
							onChange={setTenant}
							ariaLabel={`Tenant for ${TITLE}`}
						/>
						<WidgetSelect
							value={year}
							options={yearOptions()}
							onChange={setYear}
							ariaLabel={`Year for ${TITLE}`}
						/>
					</>
				}
				onExpand={expanded.show}
				status={cardStatus(query, (data) => hasNoApproved(data))}
				emptyText={approvedEmptyText(year)}
				forbiddenText={`You don't have access to Reporting for ${label}.`}
				onRetry={query.refetch}
				truncated={query.data?.truncated}
			>
				{query.data && (
					<ApprovedTrend
						data={query.data}
						ownLabel={ownLabel}
						context={`${label} · ${year}`}
					/>
				)}
			</DashboardCard>
			<ReportsApprovedExpanded
				key={expanded.session}
				open={expanded.open}
				onClose={expanded.hide}
				title={TITLE}
				choice={choice}
				initialTenant={tenant}
				initialYear={year}
				renderChart={(data, onlyTypes, context) => (
					<ApprovedTrend
						data={data}
						onlyTypes={onlyTypes}
						ownLabel={ownLabel}
						context={context}
					/>
				)}
			/>
		</>
	);
}

export default ReportsApprovedTrendWidget;
